import './SegmentationLabelingTable.scss';
import cn from 'classnames';
import {
  Dispatch,
  MouseEvent as ReactMouseEvent,
  MouseEventHandler,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react';
import * as Dfns from 'date-fns/fp';
import * as R from 'ramda';
import {
  GroupedSegment,
  isBillingSegmentName,
  RawBillingSegmentMapEntry
} from '@blisspointmedia/bpm-types/dist/SegmentationMapping';
import { MdInfoOutline, MdPersonOutline, MdSmartToy } from 'react-icons/md';
import { CopyButton } from '@Components/CopyButton';
import { DatePicker } from '@Components/DatePicker';
import { Checkbox } from '@Components/FormCheck';
import { InfoTooltip } from '@Components/InfoTooltip';
import { InteractiveDiv, InteractiveSpan } from '@Components/InteractiveEl';
import { Dropdown } from '@Components/Dropdown';
import { DropdownVariant } from '@Components/Dropdown/dropdown-constants';
import { FrozenColumnsTable } from '@Components/FrozenColumnsTable';
import { CellRenderer, CornerLocation, Header } from '@Components/StickyTable';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Size } from '@Components/designConstants';
import { Brand30, Neutral300, Neutral400 } from '../../utils/colors';
import { DateRange } from '../../utils/date-utils';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { useMap } from '../../utils/hooks/useData';
import {
  AUTOMATION_USER_EMAIL,
  CustomSegmentsData,
  EditsMapAction,
  NAMES_IDS_OPTIONS,
  PresetSegmentNames,
  SegmentEditsMap,
  SegmentRow,
  TIMESTAMP_EMAIL_OPTIONS
} from './segmentation-mapping-constants';
import { MixedBillingSegmentData } from './MixedBillingSegmentModal';
import { isNewBillingSegmentValue } from './segmentationMappingUtils';

interface SegmentationLabelingTableProps {
  data: SegmentRow[];
  customSegments: CustomSegmentsData[] | undefined;
  filteredCustomSegments: CustomSegmentsData[] | undefined;
  editsMap: SegmentEditsMap;
  selectedRows: Record<string, SegmentRow>;
  dispatchEditsMap: Dispatch<EditsMapAction>;
  setSelectedRows: Dispatch<SetStateAction<Record<string, SegmentRow>>>;
  dataGranularity: 'ad' | 'ad_group' | 'campaign';
  groupedSegments: Record<string, GroupedSegment[]>;
  selectedDateRange: Partial<DateRange> | null;
  setSelectedDateRange: Dispatch<SetStateAction<DateRange | null>>;
  setMixedBillingSegmentRow: Dispatch<SetStateAction<MixedBillingSegmentData | null>>;
  // True on the Partially Labeled and Labeled tabs only.
  showLastModified: boolean;
}

export type { SegmentRowEdit } from './segmentation-mapping-constants';

export const resolveBillingSegmentValue = (
  entries: RawBillingSegmentMapEntry[],
  dateRange: Partial<DateRange> | null,
  segmentName: string
): string | null => {
  const referenceStart = dateRange?.start ?? null;
  const referenceEnd = dateRange?.end ?? null;
  if (!referenceStart) {
    return 'Select Dates';
  }

  const rangeEnd = referenceEnd ?? referenceStart;
  const overlapping = Array.isArray(entries)
    ? entries.filter(
        e => e.start_date <= rangeEnd && (e.end_date === null || e.end_date >= referenceStart)
      )
    : [];

  if (overlapping.length === 0) {
    return 'Unlabeled';
  }

  const sorted = [...overlapping].sort((a, b) => a.start_date.localeCompare(b.start_date));
  const hasGap = sorted.slice(0, -1).some((entry, i) => {
    if (entry.end_date === null) {
      return false;
    }
    const d = new Date(entry.end_date);
    d.setUTCDate(d.getUTCDate() + 1);
    const dayAfter = d.toISOString().split('T')[0];
    return sorted[i + 1].start_date > dayAfter;
  });

  const firstValue = sorted[0][segmentName];
  if (
    hasGap ||
    sorted.some(e => e[segmentName] !== firstValue) ||
    !sorted.some(
      e => e.start_date <= referenceStart && (e.end_date === null || e.end_date >= referenceStart)
    ) ||
    (referenceEnd != null &&
      !sorted.some(
        e => e.start_date <= referenceEnd && (e.end_date === null || e.end_date >= referenceEnd)
      ))
  ) {
    return 'Mixed';
  }

  return firstValue ?? 'Unlabeled';
};

interface TwoOptionToggleProps<T extends string> {
  options: readonly [T, T];
  selectedOption: T;
  onChange: () => void;
}

const TwoOptionToggle = <T extends string>({
  options,
  selectedOption,
  onChange
}: TwoOptionToggleProps<T>) => (
  <SegmentedControlButton<T>
    options={[...options]}
    selectedOption={selectedOption}
    onChange={onChange}
    size={Size.SM}
  />
);

interface RevealCopyCellProps {
  className: string;
  displayText: string | null | undefined;
  revealedText: string | null | undefined;
  isRevealed: boolean;
  onHoverChange: (hovering: boolean) => void;
  copyText: string;
  leadingIcon?: ReactNode;
}

// Shared by Account/Campaign/Ad Group/Ad ID reveal + the Last Modified email
// reveal — hover (or the column's "show all" toggle) swaps the display text
// for the revealed one, and the icon copies the revealed value.
const RevealCopyCell = ({
  className,
  displayText,
  revealedText,
  isRevealed,
  onHoverChange,
  copyText,
  leadingIcon
}: RevealCopyCellProps) => (
  <div className={cn('left-cell-div', className)}>
    {leadingIcon}
    <span className='cell-text'>{isRevealed ? revealedText : displayText}</span>
    <CopyButton
      text={copyText}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    />
  </div>
);

// None's value is null, so its label is its key. A saved None is not in the menu options,
// and the shared Dropdown shows such a value's key as its label.
const segmentOptionKey = ({ label, value }: { label: string; value: number | null }): string =>
  value === null ? label : String(value);

export const SegmentationLabelingTable = ({
  data,
  customSegments,
  filteredCustomSegments,
  editsMap,
  selectedRows,
  dispatchEditsMap,
  setSelectedRows,
  dataGranularity,
  groupedSegments,
  selectedDateRange,
  setSelectedDateRange,
  setMixedBillingSegmentRow,
  showLastModified
}: SegmentationLabelingTableProps) => {
  const { hasBillingCategory, orderedSegments } = useMemo(() => {
    const allSegments = filteredCustomSegments ?? [];
    const index = allSegments.findIndex(s => s.segmentName === PresetSegmentNames.BILLING_CATEGORY);
    if (index === -1) {
      return { hasBillingCategory: false, orderedSegments: allSegments };
    }
    const others = allSegments.filter((_, i) => i !== index);
    return {
      hasBillingCategory: true,
      orderedSegments: [...others.slice(0, -2), allSegments[index], ...others.slice(-2)]
    };
  }, [filteredCustomSegments]);

  const [selectAll, setSelectAll] = useState(false);
  const [focusedCell, setFocusedCell] = useState('');
  const [resizeActive, setResizeActive] = useState(false);
  const [resizedLeftWidth, setResizedLeftWidth] = useState<number | undefined>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const containerSize = useContainerSize(containerRef, { widthOnly: true });

  const selectedRowsSet = useMemo(() => new Set(Object.keys(selectedRows)), [selectedRows]);

  const selectRow = useCallback(
    (row: SegmentRow) => {
      const { campaign_key } = row;
      if (Object.hasOwn(selectedRows, campaign_key)) {
        setSelectedRows(current => R.omit([campaign_key], current));
      } else {
        setSelectedRows(current => ({ ...current, [campaign_key]: row }));
      }
    },
    [selectedRows, setSelectedRows]
  );

  const dataWithEdits = useMemo(
    () => data.map(row => editsMap[row.campaign_key] ?? row),
    [editsMap, data]
  );

  const dataMap = useMemo(() => {
    const output: Record<string, SegmentRow> = {};
    data.forEach(row => {
      output[row.campaign_key] = row;
    });
    return output;
  }, [data]);

  const [showAllAccountIDs, setShowAllAccountIDs] = useState(false);
  const [showAccountID, setShowAccountID] = useMap<string, boolean>({});
  const [showAllCampaignIDs, setShowAllCampaignIDs] = useState(false);
  const [showCampaignID, setShowCampaignID] = useMap<string, boolean>({});
  const [showAllAdGroupIDs, setShowAllAdGroupIDs] = useState(false);
  const [showAdGroupID, setShowAdGroupID] = useMap<string, boolean>({});
  const [showAllAdIDs, setShowAllAdIDs] = useState(false);
  const [showAdID, setShowAdID] = useMap<string, boolean>({});
  const [showAllLastModifiedBy, setShowAllLastModifiedBy] = useState(false);
  const [showLastModifiedBy, setShowLastModifiedBy] = useMap<string, boolean>({});

  const getSegmentStyles = useCallback(
    (
      segmentName: string,
      rowToUse: SegmentRow,
      values?: { label: string; value: number | null }[],
      groupedSegmentList?: GroupedSegment[]
    ) => {
      let valueType: string;

      if (groupedSegmentList) {
        const hasEdits = groupedSegmentList.some(segment =>
          Object.hasOwn(rowToUse.edits ?? {}, segment.segmentName)
        );
        const hasActualValues =
          values &&
          values.length > 0 &&
          values.some(v => v.value !== null && v.value !== undefined && v.label !== 'None');
        const hasNoneSelected = values && values.length > 0 && values.some(v => v.label === 'None');

        if (hasEdits) {
          valueType = 'edit';
        } else if (hasActualValues || hasNoneSelected) {
          valueType = 'saved';
        } else {
          valueType = 'empty';
        }
      } else if (isBillingSegmentName(segmentName)) {
        valueType = 'saved';
      } else {
        const hasEdits = Object.hasOwn(rowToUse.edits ?? {}, segmentName);
        const hasSavedValue = rowToUse[segmentName] !== null && rowToUse[segmentName] !== undefined;

        if (hasEdits) {
          valueType = 'edit';
        } else if (hasSavedValue) {
          valueType = 'saved';
        } else {
          valueType = 'empty';
        }
      }

      const focusedBorderColorByType: Record<string, string> = {
        edit: Neutral400,
        saved: Neutral300
      };
      const focusedBorderColor = focusedBorderColorByType[valueType] ?? Brand30;
      return {
        valueType,
        styles: {
          control: (baseStyles: Record<string, unknown>, state: { isFocused: boolean }) => ({
            ...baseStyles,
            width: '100%',
            outline: 'none !important',
            border: state.isFocused
              ? `1px solid ${focusedBorderColor} !important`
              : '1px solid transparent !important',
            boxShadow: 'none !important',
            '&:hover': {
              borderColor: state.isFocused
                ? `${focusedBorderColor} !important`
                : 'transparent !important'
            },
            '&:focus': {
              outline: 'none !important',
              boxShadow: 'none !important'
            }
          })
        }
      };
    },
    []
  );

  const rightTableHeaders: Header[] = useMemo(() => {
    const inBillingGroup = (n: string | undefined) =>
      isBillingSegmentName(n) || (hasBillingCategory && n === PresetSegmentNames.BILLING_CATEGORY);
    return orderedSegments.map((segment, index, arr) => {
      const isDisabled = ['Channel', 'Platform'].includes(segment.segmentName);
      // if the next segment is in the billing group and the current segment is not, add a border left to the next segment's column
      return {
        label: segment.segmentName,
        name: segment.segmentName,
        className:
          !inBillingGroup(segment.segmentName) && inBillingGroup(arr[index + 1]?.segmentName)
            ? 'billing-segment-border-left'
            : undefined,
        width:
          segment.segmentId === 'null' && !isBillingSegmentName(segment.segmentName) ? 400 : 200,
        renderer: (row: SegmentRow) => {
          if (
            segment.segmentId === 'null' &&
            row &&
            groupedSegments &&
            !isBillingSegmentName(segment.segmentName)
          ) {
            // When segmentId is null, segmentName is the group name
            const groupName = segment.segmentName;
            if (!groupName || !groupedSegments[groupName]) {
              return null;
            }
            const rowToUse = editsMap[row.campaign_key] || row;
            const { campaign_key } = rowToUse;

            const options: { label: string; value: number | null }[] = segment.values.map(
              value => ({ label: value.valueName, value: value.valueId })
            );
            const noneOption = { label: 'None', value: null };

            const yesGroupedSegments = groupedSegments[groupName].filter(
              bs =>
                (Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName) &&
                  (rowToUse.edits?.[bs.segmentName] as { label: string })?.label === 'Yes') ||
                (rowToUse[bs.segmentName] === 'Yes' &&
                  !Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName))
            );
            const noGroupedSegments = groupedSegments[groupName].filter(
              bs =>
                (Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName) &&
                  (rowToUse.edits?.[bs.segmentName] as { label: string })?.label === 'No') ||
                (rowToUse[bs.segmentName] === 'No' &&
                  !Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName))
            );

            const values: { label: string; value: number | null }[] =
              yesGroupedSegments.length === 0 && noGroupedSegments.length > 0
                ? [noneOption]
                : groupedSegments[groupName]
                    .filter(
                      bs =>
                        (Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName) &&
                          (rowToUse.edits?.[bs.segmentName] as { label: string })?.label ===
                            'Yes') ||
                        (rowToUse[bs.segmentName] === 'Yes' &&
                          !(
                            Object.hasOwn(rowToUse.edits ?? {}, bs.segmentName) &&
                            (rowToUse.edits?.[bs.segmentName] as { label: string })?.label === 'No'
                          ))
                    )
                    .map(bs => ({
                      label: bs.segmentName,
                      value: bs.segmentId
                    }));

            const keysToApply = selectedRowsSet.has(campaign_key)
              ? Array.from(selectedRowsSet)
              : [campaign_key];

            const booleanEdits = (trueSegments: { label: string; value: number | null }[]) => {
              const trueEdits = trueSegments
                .map(sel => {
                  const groupedSegment = groupedSegments[groupName].find(
                    s => s.segmentId === sel.value
                  );
                  if (!groupedSegment) {
                    return null;
                  }
                  const yesValue = customSegments
                    ?.find(s => s.segmentName === groupedSegment.segmentName)
                    ?.values?.find(v => v.valueName === 'Yes');
                  if (!yesValue?.valueId) {
                    return null;
                  }
                  return {
                    label: groupedSegment.segmentName,
                    value: yesValue.valueId,
                    valueName: yesValue.valueName
                  };
                })
                .filter(
                  (item): item is { label: string; value: number; valueName: string } =>
                    item !== null
                )
                .reduce(
                  (acc, item) => {
                    acc[item.label] = {
                      label: item.valueName,
                      value: item.value
                    };
                    return acc;
                  },
                  {} as Record<string, { label: string; value: number | string }>
                );

              const falseSegments = groupedSegments[groupName].filter(
                bs => !trueSegments.map(s => s.value).includes(bs.segmentId)
              );
              const falseEdits = falseSegments
                .map(sel => {
                  const groupedSegment = groupedSegments[groupName].find(
                    s => s.segmentId === sel.segmentId
                  );
                  if (!groupedSegment) {
                    return null;
                  }
                  const noValue = customSegments
                    ?.find(s => s.segmentName === groupedSegment.segmentName)
                    ?.values?.find(v => v.valueName === 'No');
                  if (!noValue?.valueId) {
                    return null;
                  }
                  return {
                    label: groupedSegment.segmentName,
                    value: noValue.valueId
                  };
                })
                .filter((item): item is { label: string; value: number } => item !== null)
                .reduce(
                  (acc, item) => {
                    acc[item.label] = {
                      label: 'No',
                      value: item.value
                    };
                    return acc;
                  },
                  {} as Record<string, { label: string; value: number | string }>
                );

              return { ...trueEdits, ...falseEdits };
            };

            const { valueType: booleanValueType } = getSegmentStyles(
              '',
              rowToUse,
              values,
              groupedSegments[groupName]
            );

            const hasNoneSelected = values.some(v => v.value === noneOption.value);
            const hasSavedValues = groupedSegments[groupName].some(
              s => rowToUse[s.segmentName] != null
            );
            let selectOptions: { label: string; value: number | null }[];
            if (hasNoneSelected) {
              selectOptions = hasSavedValues ? options : [noneOption];
            } else if (values.length === 0) {
              selectOptions = [noneOption, ...options];
            } else {
              selectOptions = options;
            }

            const noneIsFixed = hasNoneSelected && hasSavedValues;

            return (
              <div
                className={cn('grouped-segment-container', `${booleanValueType}-boolean-unfocused`)}
              >
                <Dropdown
                  variant={DropdownVariant.CELL}
                  searchable
                  multiSelect
                  // A saved None cannot be removed, so it shows as text instead of a removable chip.
                  showChips={!noneIsFixed}
                  clearable={!noneIsFixed}
                  ariaLabel={groupName}
                  placeholder='Select Products'
                  // if unlabeled and None is selected, don't show any options. If nothing has been selected, show None as an option
                  options={selectOptions.map(o => ({ label: o.label, value: segmentOptionKey(o) }))}
                  value={values.map(segmentOptionKey)}
                  onChange={keys => {
                    const selection = [noneOption, ...options].filter(o =>
                      keys.includes(segmentOptionKey(o))
                    );
                    // if unlabeled and nothing is selected, delete all edits
                    if (selection.length === 0 && !hasSavedValues) {
                      dispatchEditsMap({
                        type: 'DELETE_SEGMENT_EDIT',
                        segmentNames: groupedSegments[groupName].map(s => s.segmentName),
                        campaign_keys: keysToApply
                      });
                      return;
                    }

                    const edits = booleanEdits(selection);

                    const rowsToApply = selectedRowsSet.has(campaign_key)
                      ? Object.values(selectedRows)
                      : [rowToUse];

                    dispatchEditsMap({
                      type: 'UPDATE_SEGMENT_EDIT',
                      updates: [{ rows: rowsToApply, edits }]
                    });
                  }}
                />
              </div>
            );
          }
          if (row) {
            const rowToUse = editsMap[row.campaign_key] || row;
            const { campaign_key } = rowToUse;
            // styles intentionally not destructured: tinuiti's <Dropdown overlayPlacement={Placement.RIGHT} size={Size.LG}> does not accept the
            // react-select `styles` prop. Per-valueType outline is applied via the
            // wrapping className below and CSS in SegmentationLabelingTable.scss.
            const { valueType } = getSegmentStyles(segment.segmentName, rowToUse);

            let entries: RawBillingSegmentMapEntry[] = [];
            let val: string | null;
            const segmentEdit = rowToUse.edits?.[segment.segmentName];
            // Only a value-edit carries .label; billing edits are arrays and committed values are strings.
            const editLabel =
              segmentEdit && !Array.isArray(segmentEdit) && typeof segmentEdit === 'object'
                ? segmentEdit.label
                : undefined;
            val =
              (Object.hasOwn(rowToUse.edits ?? {}, segment.segmentName)
                ? editLabel
                : rowToUse[segment.segmentName]) ||
              // platform and channel will be lowercased from redshift
              rowToUse[segment.segmentName.toLowerCase()];

            if (isDisabled) {
              return <div>{val}</div>;
            }
            if (isBillingSegmentName(segment.segmentName)) {
              // If there are (array-shaped) billing edits, use them as the entries. A billing
              // edit can be a non-array `{ label, value }` object from a non-billing edit path;
              // guard with Array.isArray so `.map` can't throw "x.map is not a function"
              // (DSH-33752).
              const billingEdit = rowToUse.edits?.[segment.segmentName];
              entries =
                Array.isArray(billingEdit) && billingEdit.length
                  ? billingEdit.map(
                      (e: { label: string; startDate: string; endDate: string | null }) => ({
                        [segment.segmentName]: e.label,
                        start_date: e.startDate,
                        end_date: e.endDate ?? null
                      })
                    )
                  : (rowToUse[segment.segmentName] as RawBillingSegmentMapEntry[]);

              val = resolveBillingSegmentValue(entries, selectedDateRange, segment.segmentName);
              const billingValueType =
                val === 'Mixed' ||
                (selectedDateRange?.start &&
                  !isNewBillingSegmentValue(
                    { label: val || '', startDate: selectedDateRange?.start || '' },
                    rowToUse[segment.segmentName] ?? [],
                    segment.segmentName
                  ))
                  ? 'saved'
                  : 'edit';
              return (
                <div className={billingValueType}>
                  <span
                    className={cn('cell-text', {
                      'cell-text-placeholder': !val || val === 'Select Dates' || val === 'Unlabeled'
                    })}
                  >
                    {val === 'Mixed' ? (
                      <InteractiveSpan
                        className='mixed-billing-segment-clickable'
                        onClick={() =>
                          setMixedBillingSegmentRow({
                            segmentName: segment.segmentName,
                            entries,
                            committedEntries: rowToUse[segment.segmentName]
                          })
                        }
                      >
                        {val}
                        <MdInfoOutline className='mixed-billing-segment-info-icon' />
                      </InteractiveSpan>
                    ) : (
                      val
                    )}
                  </span>
                </div>
              );
            }

            if (focusedCell === `${segment.segmentName}_${campaign_key}`) {
              return (
                <div className={valueType}>
                  <Dropdown
                    size={Size.LG}
                    searchable
                    clearable
                    variant={DropdownVariant.CELL}
                    menuPortalTarget={document.body}
                    value={val || null}
                    placeholder='Select label'
                    onChange={selection => {
                      const selectedOption = segment.values.find(
                        v => String(v.valueId) === selection
                      );
                      // Issue an update when selecting an option from the dropdown, and
                      // issue a delete when selecting the "x" from the input
                      if (selectedOption) {
                        const rowsToApply = selectedRowsSet.has(campaign_key)
                          ? Object.values(selectedRows)
                          : [rowToUse];
                        dispatchEditsMap({
                          type: 'UPDATE_SEGMENT_EDIT',
                          updates: [
                            {
                              rows: rowsToApply,
                              edits: {
                                [segment.segmentName]: {
                                  label: selectedOption.valueName,
                                  value: selectedOption.valueId
                                }
                              }
                            }
                          ]
                        });
                      } else {
                        const keys = selectedRowsSet.has(campaign_key)
                          ? Array.from(selectedRowsSet)
                          : [campaign_key];
                        dispatchEditsMap({
                          type: 'DELETE_SEGMENT_EDIT',
                          segmentNames: [segment.segmentName],
                          campaign_keys: keys
                        });
                      }
                    }}
                    options={segment.values.map(value => ({
                      label: value.valueName,
                      value: String(value.valueId)
                    }))}
                    reactSelectProps={{
                      autoFocus: true,
                      defaultMenuIsOpen: true,
                      onBlur: () => setFocusedCell('')
                    }}
                  />
                </div>
              );
            }

            return (
              <InteractiveDiv
                className={`${valueType}-unfocused`}
                onClick={() => setFocusedCell(`${segment.segmentName}_${campaign_key}`)}
              >
                <span className={cn('cell-text', { 'cell-text-placeholder': !val })}>
                  {val || 'Select Label'}
                </span>
              </InteractiveDiv>
            );
          }
          return <div />;
        }
      };
    });
  }, [
    orderedSegments,
    hasBillingCategory,
    dispatchEditsMap,
    editsMap,
    focusedCell,
    customSegments,
    groupedSegments,
    selectedRows,
    selectedRowsSet,
    getSegmentStyles,
    selectedDateRange,
    setMixedBillingSegmentRow
  ]);

  // Making this its own thing to prevent rerenders that happened when using rightTableHeaders.
  const rightHeaderNames = useMemo(
    () => rightTableHeaders.map(header => header.name ?? ''),
    [rightTableHeaders]
  );

  const customLeftRenderer: CellRenderer<Record<string, unknown>> = useCallback(
    ({ data: rowData, style = {}, classes = [] }) => {
      const {
        campaign_key,
        account_name,
        account_id,
        campaign_name,
        campaign_id,
        ad_group_name,
        ad_group_id,
        ad_name,
        ad_id,
        last_modified,
        last_modified_by
      } = rowData as unknown as SegmentRow;

      return (
        <div style={style} className={cn(classes, 'left-cell')}>
          <div className='check-box-container'>
            <Checkbox
              className='checkbox'
              checked={Object.hasOwn(selectedRows, campaign_key)}
              onChange={() => selectRow(dataMap[campaign_key])}
            />
          </div>
          <RevealCopyCell
            className='account'
            displayText={account_name}
            revealedText={account_id}
            isRevealed={showAllAccountIDs || Boolean(showAccountID[campaign_key])}
            onHoverChange={hovering => setShowAccountID(campaign_key, hovering)}
            copyText={account_id}
          />
          <RevealCopyCell
            className='campaign'
            displayText={campaign_name}
            revealedText={campaign_id}
            isRevealed={showAllCampaignIDs || Boolean(showCampaignID[campaign_key])}
            onHoverChange={hovering => setShowCampaignID(campaign_key, hovering)}
            copyText={campaign_id}
          />
          {(dataGranularity === 'ad_group' || dataGranularity === 'ad') && (
            <RevealCopyCell
              className='ad_group'
              displayText={ad_group_name}
              revealedText={ad_group_id}
              isRevealed={showAllAdGroupIDs || Boolean(showAdGroupID[campaign_key])}
              onHoverChange={hovering => setShowAdGroupID(campaign_key, hovering)}
              copyText={ad_group_id ?? ''}
            />
          )}
          {dataGranularity === 'ad' && (
            <RevealCopyCell
              className='ad'
              displayText={ad_name}
              revealedText={ad_id}
              isRevealed={showAllAdIDs || Boolean(showAdID[campaign_key])}
              onHoverChange={hovering => setShowAdID(campaign_key, hovering)}
              copyText={ad_id ?? ''}
            />
          )}
          {showLastModified &&
            (last_modified ? (
              <RevealCopyCell
                className='last-modified'
                leadingIcon={
                  last_modified_by === AUTOMATION_USER_EMAIL ? <MdSmartToy /> : <MdPersonOutline />
                }
                displayText={Dfns.format('MMM d, yyyy h:mm a', new Date(last_modified))}
                revealedText={last_modified_by}
                isRevealed={showAllLastModifiedBy || Boolean(showLastModifiedBy[campaign_key])}
                onHoverChange={hovering => setShowLastModifiedBy(campaign_key, hovering)}
                copyText={last_modified_by ?? ''}
              />
            ) : (
              <div className='left-cell-div last-modified'>
                <span className='cell-text cell-text-placeholder'>—</span>
              </div>
            ))}
        </div>
      );
    },
    [
      dataGranularity,
      dataMap,
      selectRow,
      selectedRows,
      setShowAccountID,
      setShowAdGroupID,
      setShowAdID,
      setShowCampaignID,
      showAccountID,
      showAdGroupID,
      showAdID,
      showAllAccountIDs,
      showAllAdGroupIDs,
      showAllAdIDs,
      showAllCampaignIDs,
      showAllLastModifiedBy,
      showCampaignID,
      showLastModified,
      showLastModifiedBy,
      setShowLastModifiedBy
    ]
  );

  const [rightData, setRightData] = useState<{ originalRowIndex: number; content: unknown }[][]>(
    []
  );

  const sortRightData = useCallback(
    (columnName: string, direction: string) => {
      const tempData = [...dataWithEdits];

      if (direction === 'up') {
        tempData.sort((a, b) => {
          const valA = a[columnName] || '';
          const valB = b[columnName] || '';
          return valA.localeCompare(valB);
        });
      }
      if (direction === 'down') {
        tempData.sort((a, b) => {
          const valA = a[columnName] || '';
          const valB = b[columnName] || '';
          return valB.localeCompare(valA);
        });
      }

      const originalIndexMap: Record<string, number> = {};
      for (let i = 0; i < dataWithEdits.length; i++) {
        originalIndexMap[dataWithEdits[i].campaign_key] = i;
      }

      const sortedData: { originalRowIndex: number; content: unknown }[][] = [];
      for (let i = 0; i < tempData.length; ++i) {
        const obj = tempData[i];
        const row: { originalRowIndex: number; content: unknown }[] = [];
        for (const { name } of rightTableHeaders) {
          const content = obj[name ?? 'undefined'];
          row.push({
            originalRowIndex: originalIndexMap[obj.campaign_key],
            content: content == null ? '' : content
          });
        }
        sortedData.push(row);
      }
      setRightData(sortedData);
    },
    [dataWithEdits, rightTableHeaders]
  );

  const [sortMap, setSortMapValue, setSortMap] = useMap<string, string>({});

  const onSort = useCallback(
    (propertyName: string, event: ReactMouseEvent) => {
      // Prevent sorting when clicking on the name/id toggle
      if (event.target instanceof HTMLElement) {
        const { classList } = event.target;
        if (
          classList.contains('segmented-item') ||
          classList.contains('segmented-control') ||
          event.target.closest('.segmented-control')
        ) {
          return;
        }
      }
      const sortingTransitions: Record<string, string> = { off: 'up', up: 'down', down: 'off' };
      setSortMap({});
      const next = sortingTransitions[sortMap[propertyName] || 'off'];
      setSortMapValue(propertyName, next);
      sortRightData(propertyName, next);
    },
    [setSortMap, setSortMapValue, sortMap, sortRightData]
  );

  const customCornerRenderer = useCallback(
    (corner: CornerLocation) => {
      if (corner !== 'nw') {
        return <div />;
      }
      return (
        <div
          className='sticky-table-cell'
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            backgroundColor: Neutral300
          }}
        >
          <div className='check-box-container'>
            <Checkbox
              checked={selectAll}
              onChange={() => {
                if (selectAll) {
                  setSelectedRows({});
                } else {
                  const selectAllMap: Record<string, SegmentRow> = {};
                  if (data) {
                    for (const row of data) {
                      selectAllMap[row.campaign_key] = row;
                    }
                  }
                  setSelectedRows(current => ({ ...current, ...selectAllMap }));
                }
                setSelectAll(!selectAll);
              }}
            />
          </div>
          <InteractiveDiv
            className={cn('include-sort-header-caret', 'account', sortMap.account_name)}
            onClick={e => onSort('account_name', e)}
          >
            Account
            <TwoOptionToggle
              options={NAMES_IDS_OPTIONS}
              selectedOption={showAllAccountIDs ? 'IDs' : 'Names'}
              onChange={() => setShowAllAccountIDs(prev => !prev)}
            />
          </InteractiveDiv>
          <InteractiveDiv
            className={cn('include-sort-header-caret', 'campaign', sortMap.campaign_name)}
            onClick={e => onSort('campaign_name', e)}
          >
            Campaign
            <TwoOptionToggle
              options={NAMES_IDS_OPTIONS}
              selectedOption={showAllCampaignIDs ? 'IDs' : 'Names'}
              onChange={() => setShowAllCampaignIDs(prev => !prev)}
            />
          </InteractiveDiv>
          {(dataGranularity === 'ad_group' || dataGranularity === 'ad') && (
            <InteractiveDiv
              className={cn('include-sort-header-caret', 'ad_group', sortMap.ad_group_name)}
              onClick={e => onSort('ad_group_name', e)}
            >
              Ad Group
              <TwoOptionToggle
                options={NAMES_IDS_OPTIONS}
                selectedOption={showAllAdGroupIDs ? 'IDs' : 'Names'}
                onChange={() => setShowAllAdGroupIDs(prev => !prev)}
              />
            </InteractiveDiv>
          )}
          {dataGranularity === 'ad' && (
            <InteractiveDiv
              className={cn('include-sort-header-caret', 'ad', sortMap.ad_name)}
              onClick={e => onSort('ad_name', e)}
            >
              Ad
              <TwoOptionToggle
                options={NAMES_IDS_OPTIONS}
                selectedOption={showAllAdIDs ? 'IDs' : 'Names'}
                onChange={() => setShowAllAdIDs(prev => !prev)}
              />
            </InteractiveDiv>
          )}
          {showLastModified && (
            <InteractiveDiv
              className={cn('include-sort-header-caret', 'last-modified', sortMap.last_modified)}
              onClick={e => onSort('last_modified', e)}
            >
              Last Modified
              <TwoOptionToggle
                options={TIMESTAMP_EMAIL_OPTIONS}
                selectedOption={showAllLastModifiedBy ? 'Email' : 'Timestamp'}
                onChange={() => setShowAllLastModifiedBy(prev => !prev)}
              />
            </InteractiveDiv>
          )}
        </div>
      );
    },
    [
      selectAll,
      sortMap.account_name,
      sortMap.campaign_name,
      sortMap.ad_group_name,
      sortMap.last_modified,
      sortMap.ad_name,
      showAllAccountIDs,
      showAllCampaignIDs,
      dataGranularity,
      showAllAdGroupIDs,
      showAllAdIDs,
      showLastModified,
      showAllLastModifiedBy,
      setSelectedRows,
      data,
      onSort
    ]
  );

  const leftDataFields = useMemo(() => {
    let leftFields = ['campaign_key', 'account_name', 'account_id', 'campaign_name', 'campaign_id'];
    if (dataGranularity === 'ad_group') {
      leftFields = leftFields.concat(['ad_group_name', 'ad_group_id']);
    }
    if (dataGranularity === 'ad') {
      leftFields = leftFields.concat(['ad_group_name', 'ad_group_id', 'ad_name', 'ad_id']);
    }
    if (showLastModified) {
      leftFields = leftFields.concat(['last_modified', 'last_modified_by']);
    }

    return leftFields;
  }, [dataGranularity, showLastModified]);

  const noRowsRenderer = useCallback(
    () => (data.length === 0 ? <div className='no-rows'>No rows to display.</div> : <div />),
    [data.length]
  );

  const leftSuperTopRenderer = useCallback(() => <div className='left-super-top-cell' />, []);

  const superTopRenderer: CellRenderer<unknown> = useCallback(
    ({ data: rawData, style, classes }) => {
      const superTopCellData = (rawData ?? {}) as {
        text?: string;
        hasBillingCategory?: boolean;
      };
      const isBillingHeader = superTopCellData.text === 'Billing Only Segments';
      if (!isBillingHeader) {
        return (
          <div style={style} className={cn(classes, 'super-top-cell')}>
            <div className='super-top-label'>
              {superTopCellData?.text ?? ''}
              <InfoTooltip size={Size.SM}>
                These segments will appear in reporting dashboards and do not apply to billing.
              </InfoTooltip>
            </div>
          </div>
        );
      }
      return (
        <div style={style} className={cn(classes, 'super-top-cell', 'billing')}>
          <div className='super-top-label'>
            {superTopCellData?.text ?? ''}{' '}
            <InfoTooltip size={Size.SM}>
              Billing only segments are used for billing purposes only and will not appear on
              dashboards. They are considered fully labeled when they are labeled for each day of
              the previous calendar month.
            </InfoTooltip>
          </div>
          {superTopCellData.hasBillingCategory ? (
            <div className='billing-header-row'>
              <div className='not-date-based-label'>(not date based)</div>
              <div className='billing-header-date-picker'>
                <DatePicker
                  range={selectedDateRange as DateRange}
                  onChange={setSelectedDateRange}
                  isOutsideRange={() => false}
                  allowOnlyStartDates
                  size={Size.SM}
                />
              </div>
            </div>
          ) : (
            <div className='billing-header-date-picker'>
              <DatePicker
                range={selectedDateRange as DateRange}
                onChange={setSelectedDateRange}
                isOutsideRange={() => false}
                allowOnlyStartDates
                size={Size.SM}
              />
            </div>
          )}
        </div>
      );
    },
    [selectedDateRange, setSelectedDateRange]
  );

  const superTopData = useMemo(
    () => [
      {
        span: orderedSegments.length - (hasBillingCategory ? 3 : 2),
        data: { text: 'Reporting Segments' }
      },
      {
        span: hasBillingCategory ? 3 : 2,
        data: { text: 'Billing Only Segments', hasBillingCategory }
      }
    ],
    [orderedSegments.length, hasBillingCategory]
  );

  const startingWidth = useMemo(() => {
    const containerWidth = containerSize?.width ?? 0;
    switch (dataGranularity) {
      case 'ad':
        return (3 / 4) * containerWidth;
      case 'ad_group':
        return (5 / 8) * containerWidth;
      case 'campaign':
      default:
        return (1 / 2) * containerWidth;
    }
  }, [dataGranularity, containerSize?.width]);

  const leftWidth = resizedLeftWidth ?? startingWidth;

  const leftSpacing = 24;
  const resizeFrame: MouseEventHandler<HTMLDivElement> = e => {
    if (resizeActive) {
      const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
      const halfAreaWidth = 10;
      const adjustedPosition = e.clientX - containerLeft - leftSpacing - halfAreaWidth;
      setResizedLeftWidth(adjustedPosition);
    }
  };
  return (
    <InteractiveDiv
      ref={containerRef}
      className='segmentation-labeling-table'
      onMouseUp={() => setResizeActive(false)}
      onMouseMove={e => resizeFrame(e)}
    >
      <div className='resize-container'>
        <InteractiveDiv
          className='resize-area'
          style={{ left: `${leftWidth + 24}px` }}
          onMouseDown={() => setResizeActive(true)}
        />
      </div>
      <FrozenColumnsTable
        leftWidth={leftWidth}
        leftRenderer={customLeftRenderer}
        cornerRenderer={customCornerRenderer}
        rightTableHeaders={rightTableHeaders}
        rightTableHeaderNames={rightHeaderNames}
        originalRightData={data as unknown as Record<string, unknown>[]}
        leftDataFields={leftDataFields}
        rightData={rightData}
        setRightData={setRightData}
        noRowsRenderer={noRowsRenderer}
        topHeight={60}
        superTopData={superTopData}
        superTopRenderer={superTopRenderer}
        superTopHeight={48}
        leftSuperTopRenderer={leftSuperTopRenderer}
      />
    </InteractiveDiv>
  );
};
